import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Package, 
  Search, 
  CheckCircle, 
  Clock, 
  XCircle, 
  Truck, 
  Calendar, 
  User, 
  MapPin,
  Mail,
  Phone,
  FileText,
  Printer,
  AlertCircle,
  Loader2,
  PackageCheck
} from 'lucide-react';
import { formatPrice, getDomain } from '@/lib/utils';
import { useStoreInfo } from '@/lib/store';

interface OrderDetail {
  payment_id: string;
  product_name: string;
  product_variant: string;
  variant_qty: number;
  status?: string;
  unit_price: number;
  line_amount?: number;
  pricing_type?: string;
}

interface PaymentInfo {
  store_id: number;
  order_id: string;
  date: string;
  goods_amount: number;
  shipping_fee: number;
  shipmentType: string;
  note: string;
  orderStatus?: string;
  customer_name?: string;
  customer_email?: string;
  customer_phone?: string;
  customer_address?: string;
  shipping_address?: string;
  vat?: number;
  payment_gateway?: string;
  payment_method?: string;
}

interface OrderResponse {
  status: boolean;
  message: string;
  orderDetails?: OrderDetail[];
  payment_info?: PaymentInfo;
  order_status?: string;
}

export function TrackPage() {
  const [searchParams] = useSearchParams();
  const [trackingId, setTrackingId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [orderStatus, setOrderStatus] = useState<OrderResponse | null>(null);
  const { storeInfo } = useStoreInfo();

  // Auto-populate and fetch if tracking ID is in URL
  useEffect(() => {
    const idFromUrl = searchParams.get('id');
    if (idFromUrl) {
      setTrackingId(idFromUrl);
      // Auto-fetch the order
      fetchOrder(idFromUrl);
    }
  }, [searchParams]);

  const fetchOrder = async (id: string) => {
    if (!id.trim()) return;
    
    setLoading(true);
    setError(null);
    setOrderStatus(null);

    try {
      const domain = getDomain();
      const response = await fetch(
        `${import.meta.env.VITE_API_ENDPOINT}/api/v1/orders/track/${encodeURIComponent(id)}?domain=${domain}`
      );
      const data: OrderResponse = await response.json();
      setOrderStatus(data);
      if (!data.status) {
        setError(data.message);
      }
    } catch (err) {
      setError('Failed to fetch order status. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    fetchOrder(trackingId);
  };

  const handlePrint = () => {
    window.print();
  };

  const getStatusColor = (status?: string) => {
    if (!status) return 'text-blue-600';
    
    const statusLower = status.toLowerCase();
    if (statusLower.includes('completed') || statusLower.includes('concluded') || statusLower.includes('delivered')) {
      return 'text-green-600';
    }
    if (statusLower.includes('pending') || statusLower.includes('processing')) {
      return 'text-yellow-600';
    }
    if (statusLower.includes('cancelled') || statusLower.includes('canceled')) {
      return 'text-red-600';
    }
    if (statusLower.includes('shipped') || statusLower.includes('transit')) {
      return 'text-blue-600';
    }
    return 'text-blue-600';
  };

  const getStatusIcon = (status?: string) => {
    if (!status) return <Package className="h-6 w-6" />;
    
    const statusLower = status.toLowerCase();
    if (statusLower.includes('completed') || statusLower.includes('concluded') || statusLower.includes('delivered')) {
      return <CheckCircle className="h-6 w-6" />;
    }
    if (statusLower.includes('pending') || statusLower.includes('processing')) {
      return <Clock className="h-6 w-6" />;
    }
    if (statusLower.includes('cancelled') || statusLower.includes('canceled')) {
      return <XCircle className="h-6 w-6" />;
    }
    if (statusLower.includes('shipped') || statusLower.includes('transit')) {
      return <Truck className="h-6 w-6" />;
    }
    return <Package className="h-6 w-6" />;
  };

  const formatDate = (timestamp: string) => {
    try {
      const date = new Date(parseInt(timestamp) * 1000);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return 'Date not available';
    }
  };

  const getDisplayStatus = () => {
    if (orderStatus?.order_status) {
      return orderStatus.order_status;
    }
    if (orderStatus?.payment_info?.orderStatus) {
      const statusMap: Record<string, string> = {
        '0': 'Pending',
        '1': 'Order Concluded',
        '2': 'Shipped',
        '3': 'Delivered',
        '4': 'Cancelled'
      };
      return statusMap[orderStatus.payment_info.orderStatus] || 'Processing';
    }
    return 'Processing';
  };

  return (
    <>
      <Helmet>
        <title>Track Order - {storeInfo?.site_name || 'Store'}</title>
        <meta name="description" content="Track your order status" />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-b from-brand-50/50 via-white to-gray-50 pb-24 md:pb-8">
        {/* Hero Section - compact and on-brand */}
        <div className="bg-gradient-to-br from-brand-500 via-brand-600 to-brand-700 py-12 sm:py-14 px-4">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ type: 'spring', stiffness: 200 }}
              className="inline-flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 bg-white/25 backdrop-blur-sm rounded-2xl mb-4 sm:mb-5 shadow-lg"
            >
              <PackageCheck className="h-8 w-8 sm:h-10 sm:w-10 text-white" />
            </motion.div>
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-display font-bold text-white mb-2 sm:mb-3 tracking-tight">
              Track Your Order
            </h1>
            <p className="text-base sm:text-lg text-white/90 max-w-md mx-auto">
              Enter your tracking number to see status and delivery details
            </p>
          </div>
        </div>

        {/* Search Section - card overlap */}
        <div className="max-w-2xl mx-auto px-4 -mt-6 sm:-mt-8">
          <motion.div
            initial={{ y: 16, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-2xl shadow-xl border border-brand-100 p-5 sm:p-6 md:p-7"
          >
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-brand-400" />
                <input
                  type="text"
                  value={trackingId}
                  onChange={(e) => setTrackingId(e.target.value)}
                  placeholder="Enter your tracking number"
                  className="w-full h-12 sm:h-14 pl-12 pr-4 rounded-xl border-2 border-brand-100 focus:border-brand-500 focus:ring-2 focus:ring-brand-100 transition-all outline-none text-gray-900 placeholder-gray-500 bg-gray-50/50 focus:bg-white"
                  required
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full h-12 sm:h-14 bg-gradient-to-r from-brand-500 to-brand-600 hover:from-brand-600 hover:to-brand-700 disabled:from-gray-300 disabled:to-gray-400 text-white font-semibold rounded-xl transition-all shadow-md hover:shadow-lg disabled:shadow-none flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-5 w-5 animate-spin" />
                    Tracking...
                  </>
                ) : (
                  <>
                    <Search className="h-5 w-5" />
                    Track Order
                  </>
                )}
              </button>
            </form>
          </motion.div>

          {/* Error Message */}
          <AnimatePresence>
            {error && (
              <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -12 }}
                className="mt-5 bg-red-50 border border-red-200 rounded-xl p-4 shadow-sm"
              >
                <div className="flex items-start gap-3">
                  <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="font-semibold text-red-900 mb-0.5">Order Not Found</h3>
                    <p className="text-sm text-red-700">{error}</p>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Order Details */}
          <AnimatePresence>
            {orderStatus?.status && orderStatus.orderDetails && orderStatus.orderDetails.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-6 sm:mt-8 space-y-5"
              >
                {/* Status Card */}
                <div className="bg-white rounded-2xl shadow-lg border border-brand-100 overflow-hidden">
                  <div className="bg-gradient-to-r from-brand-500 to-brand-600 p-5 sm:p-6 text-white">
                    <div className="flex items-center justify-between gap-4">
                      <div className="flex items-center gap-3 sm:gap-4 min-w-0">
                        <div className="p-2.5 sm:p-3 rounded-xl bg-white/20 backdrop-blur-sm flex-shrink-0 text-white">
                          {getStatusIcon(getDisplayStatus())}
                        </div>
                        <div className="min-w-0">
                          <h2 className="text-xl sm:text-2xl font-bold font-display">Order Status</h2>
                          <p className="text-base sm:text-lg font-medium opacity-95 mt-0.5 truncate">
                            {getDisplayStatus()}
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={handlePrint}
                        className="p-2.5 rounded-xl bg-white/20 hover:bg-white/30 backdrop-blur-sm transition-colors print:hidden flex-shrink-0"
                        title="Print order details"
                      >
                        <Printer className="h-5 w-5" />
                      </button>
                    </div>
                  </div>

                  {/* Order Info Grid */}
                  <div className="p-5 sm:p-6">
                    <div className="grid sm:grid-cols-2 gap-3 sm:gap-4 mb-5">
                      <div className="bg-brand-50/80 rounded-xl p-4 border border-brand-100">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-brand-500/20 rounded-xl flex items-center justify-center">
                            <Package className="h-5 w-5 text-brand-600" />
                          </div>
                          <div className="min-w-0">
                            <p className="text-xs sm:text-sm text-gray-600">Order ID</p>
                            <p className="font-bold text-gray-900 truncate">#{orderStatus.orderDetails[0].payment_id}</p>
                          </div>
                        </div>
                      </div>

                      {orderStatus.payment_info && (
                        <div className="bg-brand-50/80 rounded-xl p-4 border border-brand-100">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-brand-500/20 rounded-xl flex items-center justify-center">
                              <Calendar className="h-5 w-5 text-brand-600" />
                            </div>
                            <div className="min-w-0">
                              <p className="text-xs sm:text-sm text-gray-600">Order Date</p>
                              <p className="font-bold text-gray-900 text-sm sm:text-base">{formatDate(orderStatus.payment_info.date)}</p>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Customer & Shipping Info */}
                    {orderStatus.payment_info && (
                      <div className="grid md:grid-cols-2 gap-4 sm:gap-5 mb-5">
                        {/* Customer Info */}
                        {orderStatus.payment_info.customer_name && (
                          <div className="bg-brand-50/60 rounded-xl p-4 sm:p-5 border border-brand-100">
                            <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                              <User className="h-5 w-5 text-brand-600" />
                              Customer Details
                            </h3>
                            <div className="space-y-3">
                              <div>
                                <p className="text-sm text-gray-600">Name</p>
                                <p className="font-semibold text-gray-900">{orderStatus.payment_info.customer_name}</p>
                              </div>
                              {orderStatus.payment_info.customer_email && (
                                <div className="flex items-center gap-2">
                                  <Mail className="h-4 w-4 text-gray-500" />
                                  <p className="text-sm text-gray-700">{orderStatus.payment_info.customer_email}</p>
                                </div>
                              )}
                              {orderStatus.payment_info.customer_phone && (
                                <div className="flex items-center gap-2">
                                  <Phone className="h-4 w-4 text-gray-500" />
                                  <p className="text-sm text-gray-700">{orderStatus.payment_info.customer_phone}</p>
                                </div>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Shipping Info */}
                        {orderStatus.payment_info.shipping_address && (
                          <div className="bg-brand-50/60 rounded-xl p-4 sm:p-5 border border-brand-100">
                            <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                              <MapPin className="h-5 w-5 text-brand-600" />
                              Shipping Address
                            </h3>
                            <p className="text-gray-700 whitespace-pre-line">
                              {orderStatus.payment_info.shipping_address}
                            </p>
                            {orderStatus.payment_info.shipmentType && (
                              <div className="mt-3 pt-3 border-t border-brand-200">
                                <p className="text-sm text-gray-600">Shipping Method</p>
                                <p className="font-semibold text-gray-900">{orderStatus.payment_info.shipmentType}</p>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Order Items */}
                    <div className="bg-brand-50/40 rounded-xl p-4 sm:p-5 border border-brand-100">
                      <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                        <FileText className="h-5 w-5 text-brand-600" />
                        Order Items ({orderStatus.orderDetails.length})
                      </h3>
                      <div className="space-y-3">
                        {orderStatus.orderDetails.map((item, index) => (
                          <div key={index} className="bg-white rounded-lg p-4">
                            <div className="flex justify-between items-start mb-2">
                              <div>
                                <h4 className="font-semibold text-gray-900 flex items-center gap-2">
                                  {item.product_name}
                                  {item.pricing_type === 'quantity-tier' && (
                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-yellow-100 text-yellow-700 border border-yellow-200">
                                      Bulk Price
                                    </span>
                                  )}
                                </h4>
                              </div>
                              <p className="font-bold text-brand-600">
                                {formatPrice((item.line_amount ?? (item.unit_price * item.variant_qty)) || 0)}
                              </p>
                            </div>
                            {item.product_variant && item.product_variant.toLowerCase() !== 'nil' && (
                              <p className="text-sm text-gray-600 mb-1">
                                Variant: <span className="font-medium">{item.product_variant}</span>
                              </p>
                            )}
                            <div className="flex justify-between items-center text-sm">
                              <p className="text-gray-600">
                                Quantity: <span className="font-medium text-gray-900">{item.variant_qty}</span>
                              </p>
                              <p className="text-gray-600">
                                Unit Price: <span className="font-medium text-gray-900">{formatPrice(item.unit_price)}</span>
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Payment Summary */}
                    {orderStatus.payment_info && (
                      <div className="mt-5 bg-brand-50/60 rounded-xl p-4 sm:p-5 border border-brand-100">
                        <h3 className="font-bold text-gray-900 mb-3">Payment Summary</h3>
                        <div className="space-y-2">
                          <div className="flex justify-between text-gray-700">
                            <span>Subtotal</span>
                            <span className="font-semibold">{formatPrice(orderStatus.payment_info.goods_amount)}</span>
                          </div>
                          <div className="flex justify-between text-gray-700">
                            <span>Shipping Fee</span>
                            <span className="font-semibold">{formatPrice(orderStatus.payment_info.shipping_fee)}</span>
                          </div>
                          {'vat' in orderStatus.payment_info && (
                            <div className="flex justify-between text-gray-700">
                              <span>VAT</span>
                              <span className="font-semibold">{formatPrice(orderStatus.payment_info.vat || 0)}</span>
                            </div>
                          )}
                          <div className="border-t border-brand-200 pt-2 mt-2">
                            <div className="flex justify-between text-lg font-bold text-gray-900">
                              <span>Total</span>
                              <span className="text-brand-600">
                                {formatPrice(
                                  (orderStatus.payment_info.goods_amount || 0) +
                                  (orderStatus.payment_info.shipping_fee || 0) +
                                  (orderStatus.payment_info.vat || 0)
                                )}
                              </span>
                            </div>
                          </div>
                          {(orderStatus.payment_info.payment_method || orderStatus.payment_info.payment_gateway) && (
                            <div className="pt-2 text-sm text-gray-600">
                              Payment Method:{' '}
                              <span className="font-medium text-gray-900 capitalize">
                                {orderStatus.payment_info.payment_method || orderStatus.payment_info.payment_gateway}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Order Note */}
                    {orderStatus.payment_info?.note && (
                      <div className="mt-6 bg-amber-50 border border-amber-200 rounded-xl p-5">
                        <h3 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                          <FileText className="h-5 w-5 text-amber-600" />
                          Order Note
                        </h3>
                        <p className="text-gray-700">{orderStatus.payment_info.note}</p>
                      </div>
                    )}
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Print Styles */}
      <style>{`
        @media print {
          @page {
            margin: 1cm;
          }
          
          /* Hide navigation and non-essential elements */
          nav, header, .print\\:hidden {
            display: none !important;
          }
          
          /* Ensure content is visible */
          body {
            background: white !important;
          }
          
          /* Remove shadows and gradients for print */
          * {
            box-shadow: none !important;
            text-shadow: none !important;
          }
          
          /* Simplify backgrounds for print */
          .bg-gradient-to-r,
          .bg-gradient-to-br {
            background: white !important;
            border: 1px solid #e5e7eb !important;
          }
          
          /* Ensure text is black */
          h1, h2, h3, h4, h5, h6, p, span, div {
            color: #000 !important;
          }
          
          /* Keep structure but simplify */
          .rounded-2xl,
          .rounded-xl {
            border-radius: 8px !important;
          }
        }
      `}</style>
    </>
  );
}
